import React, { useRef, useState } from 'react';
import { IconButton, Tooltip, Spinner, useToast } from '@chakra-ui/react';
import { FiImage } from 'react-icons/fi';
import { firebase } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export default function ImageUploadButton({ editor }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { currentUser } = useAuth();
  const toast = useToast();

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Only PNG, JPEG, GIF, and WebP images are allowed.', status: 'warning', duration: 3000 });
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast({ title: 'File too large', description: 'Images must be under 5 MB.', status: 'warning', duration: 3000 });
      return;
    }

    setUploading(true);
    try {
      const uid = currentUser?.uid || 'anonymous';
      const path = `email-images/${uid}/${Date.now()}_${file.name}`;
      const storageRef = firebase.storage().ref(path);
      await storageRef.put(file);
      const url = await storageRef.getDownloadURL();

      editor?.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch (err) {
      console.error('Image upload failed:', err);
      toast({ title: 'Upload failed', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        accept={ALLOWED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Tooltip label="Insert Image" placement="top" hasArrow openDelay={400}>
        <IconButton
          icon={uploading ? <Spinner size="xs" /> : <FiImage />}
          aria-label="Insert image"
          size="xs"
          variant="ghost"
          onClick={handleClick}
          isDisabled={uploading || !editor}
        />
      </Tooltip>
    </>
  );
}
